'use client'

import { useEffect, useRef } from 'react'
import { usePathname, useSearchParams } from 'next/navigation'

const NAVIGATING_CLASS = 'is-navigating'
const NAVIGATION_START_EVENT = 'navigation-progress-start'
const FALLBACK_TIMEOUT_MS = 10_000
const MINIMUM_VISIBLE_MS = 500

const shouldShowProgress = (event: MouseEvent, link: HTMLAnchorElement) => {
  if (
    event.defaultPrevented ||
    window.matchMedia('(hover: none), (pointer: coarse)').matches ||
    event.button !== 0 ||
    event.metaKey ||
    event.ctrlKey ||
    event.shiftKey ||
    event.altKey ||
    link.hasAttribute('download') ||
    (link.target !== '' && link.target !== '_self') ||
    link.getAttribute('aria-disabled') === 'true'
  ) {
    return false
  }

  const destination = new URL(link.href, window.location.href)
  const current = new URL(window.location.href)

  if (destination.protocol !== 'http:' && destination.protocol !== 'https:') {
    return false
  }

  return (
    destination.origin !== current.origin ||
    destination.pathname !== current.pathname ||
    destination.search !== current.search
  )
}

/** Shows the branded progress cursor for links that navigate the current page. */
const NavigationProgress = () => {
  const pathname = usePathname()
  const searchParams = useSearchParams()
  const fallbackTimeoutRef = useRef<number | undefined>(undefined)
  const startedAtRef = useRef<number | null>(null)

  useEffect(() => {
    window.clearTimeout(fallbackTimeoutRef.current)

    const stopProgress = () => {
      document.documentElement.classList.remove(NAVIGATING_CLASS)
      startedAtRef.current = null
    }

    if (startedAtRef.current === null) {
      stopProgress()
      return
    }

    const elapsed = Date.now() - startedAtRef.current
    const remaining = Math.max(0, MINIMUM_VISIBLE_MS - elapsed)

    fallbackTimeoutRef.current = window.setTimeout(stopProgress, remaining)
  }, [pathname, searchParams])

  useEffect(() => {
    const stopProgress = () => {
      document.documentElement.classList.remove(NAVIGATING_CLASS)
      window.clearTimeout(fallbackTimeoutRef.current)
      startedAtRef.current = null
    }

    const startProgress = (timeoutMs = FALLBACK_TIMEOUT_MS) => {
      if (window.matchMedia('(hover: none), (pointer: coarse)').matches) return

      if (startedAtRef.current === null) {
        startedAtRef.current = Date.now()
      }

      document.documentElement.classList.add(NAVIGATING_CLASS)
      window.clearTimeout(fallbackTimeoutRef.current)
      fallbackTimeoutRef.current = window.setTimeout(stopProgress, timeoutMs)
    }

    const handleNavigationStart = (event: Event) => {
      const { isSameUrl } = (event as CustomEvent<{ isSameUrl?: boolean }>).detail ?? {}
      startProgress(isSameUrl ? MINIMUM_VISIBLE_MS : FALLBACK_TIMEOUT_MS)
    }

    const handleClick = (event: MouseEvent) => {
      const target = event.target
      const link = target instanceof Element ? target.closest<HTMLAnchorElement>('a[href]') : null

      if (link && shouldShowProgress(event, link)) {
        startProgress()
      }
    }

    const updateCursorPosition = (event: PointerEvent) => {
      document.documentElement.style.setProperty('--navigation-progress-x', `${event.clientX}px`)
      document.documentElement.style.setProperty('--navigation-progress-y', `${event.clientY}px`)
    }

    window.addEventListener('click', handleClick)
    window.addEventListener(NAVIGATION_START_EVENT, handleNavigationStart)
    window.addEventListener('pointermove', updateCursorPosition, { passive: true })
    window.addEventListener('pageshow', stopProgress)

    return () => {
      window.removeEventListener('click', handleClick)
      window.removeEventListener(NAVIGATION_START_EVENT, handleNavigationStart)
      window.removeEventListener('pointermove', updateCursorPosition)
      window.removeEventListener('pageshow', stopProgress)
      stopProgress()
    }
  }, [])

  return null
}

export default NavigationProgress
